import { Group, Space, Tabs, Text } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { getRespostasMediaEmpresa } from "@/modules/analisar-respostas/firestore";
import useGetProjetoItem from "@/modules/diagnostico/components/questionario-item/useGetProjeto";
import { getStatus } from "@/modules/diagnostico/firestore";
import { getMediaGrupoByEmpresaId } from "@/modules/media-grupo/firestore";
import getEmpresas from "@/modules/relatorio/components/card-empresa/get-empresa";
import GetEmpresasData from "@/modules/relatorio/components/graphics-nivo/useGetEmpresa";
import RelatorioComponent from "@/modules/relatorio/components/relatorio-item";
import useUserStore from "@/modules/users/store";
import { DEZ, DOIS } from "@/utils/constants";

const message = "Ocorreu um erro ao carregar os dados:";
const DEFAULT_TAB = "visao-geral";

export interface EmpresaMedia {
  id: string;
  nomeFantasia: string;
  cnpj: string;
  responsavel: { nome: string; telefone: string };
  telefone: string;
  porte: string;
  porteOriginal: string;
  endereco: { estado: string };
  categorias: string;
  media: number;
  oficial: number;
  fase: string;
  habilitada: boolean;
}

export interface EmpresaPorEstado {
  estado: string;
  quantidade: number;
}

export type TRelatorioContext = {
  empresas: TConvite[];
  empresasComMedia: EmpresaMedia[];
  empresasPorEstado: EmpresaPorEstado[];
  loadingEmpresas: boolean;
  loadingMedias: boolean;
  loadingEstados: boolean;
  errorEmpresas: string | null;
  errorMedias: string | null;
  errorEstados: string | null;
  values: {
    questionario: string;
  };
  selectedDate: string | null;
  setSelectedDate: (date: string | null) => void;
};

const RelatorioLayout = () => {
  const [values, setValues] = useState({ questionario: "" });
  const [empresas, setEmpresas] = useState<TConvite[]>([]);
  const [empresasComMedia, setEmpresasComMedia] = useState<EmpresaMedia[]>([]);
  const [empresasPorEstado, setEmpresasPorEstado] = useState<EmpresaPorEstado[]>([]);
  const [loadingEmpresas, setLoadingEmpresas] = useState(false);
  const [loadingMedias, setLoadingMedias] = useState(false);
  const [loadingEstados, setLoadingEstados] = useState(false);
  const [errorEmpresas, setErrorEmpresas] = useState<string | null>(null);
  const [errorMedias, setErrorMedias] = useState<string | null>(null);
  const [errorEstados, setErrorEstados] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const activeEmpresaId = useUserStore((state) => state.activeEmpresaId) || "";
  const projeto = useUserStore((state) => state.activeProjeto);
  const { data: projetoData } = useGetProjetoItem({ idProjeto: projeto || "" });

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setValues((values) => ({ ...values, questionario: "" }));
  }, [projeto]);

  const queryClient = useQueryClient();

  useEffect(() => {
    return () => {
      // Invalidate queries when leaving the Relatorio context to ensure fresh data on return
      void queryClient.removeQueries({ queryKey: ["empresas-detalhamento"] });
      void queryClient.removeQueries({ queryKey: ["relatorio-empresas"] });
    };
  }, [queryClient]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    loadData();
  }, [projeto, values?.questionario]);

  useEffect(() => {
    if (empresas.length > 0 && values.questionario) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      loadMediasData();
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      loadEstadosData();
    }
  }, [empresas, values.questionario, activeEmpresaId, projeto]);

  const loadData = async () => {
    if (!projeto) return;

    setLoadingEmpresas(true);
    setErrorEmpresas(null);
    try {
      const arrayEmpresas = await getEmpresas(projeto);
      setEmpresas(arrayEmpresas);
    } catch (error) {
      console.error(message, error);
      setErrorEmpresas("Não foi possível carregar a lista de empresas. Por favor, tente novamente.");
    } finally {
      setLoadingEmpresas(false);
    }
  };

  const loadMediasData = async () => {
    if (empresas.length === 0 || !values.questionario || !activeEmpresaId || !projeto) return;

    setLoadingMedias(true);
    setErrorMedias(null);
    try {
      const fasesProjeto = projetoData?.fases as Array<{ cor: string; titulo: string; checked: boolean; id: string }>;
      const minimoRespostas = projetoData?.minimoResposta || 0;

      const uniqueConvites = empresas.filter(
        (item, index, array) => array.findIndex((empresa) => empresa.idEmpresa === item.idEmpresa) === index
      );

      const convitesParaGet = [
        ...uniqueConvites.map((item) => ({
          ...item,
          idEmpresaColaboradorConvidado: item.idEmpresa
        })),
        ...empresas
      ];

      const empresaList = await GetEmpresasData(convitesParaGet);

      // eslint-disable-next-line compat/compat
      const results = await Promise.all(
        empresaList.map(async (emp) => {
          // eslint-disable-next-line compat/compat
          const [mediaSnap, respostasSnap, statusSnap] = await Promise.all([
            getMediaGrupoByEmpresaId(activeEmpresaId, values.questionario, emp.id),
            getRespostasMediaEmpresa(activeEmpresaId, emp.id),
            getStatus(activeEmpresaId, projeto, emp.id)
          ]);

          const medias = mediaSnap.docs.map((document_) => document_.data());
          const somaGrupo = medias.reduce((sum, m) => sum + (m.media || 0), 0);
          const somaOficial = medias.reduce((sum, m) => sum + (m.mediaAvaliador || 0), 0);
          const count = medias.length || 1;
          const media = somaGrupo / count / DEZ;
          const oficial = somaOficial / count / DEZ;

          const respostas = respostasSnap.docs.map((document_) => document_.data());
          const statusList = statusSnap.docs.map((document_) => document_.data());
          const faseId = statusList.length > 0 ? statusList[0]?.status : undefined;
          const faseObject = fasesProjeto?.find((f) => f.id === faseId);
          const habilitada =
            respostas.filter((r) => !r.pausado && r.idQuestionario === values.questionario).length >= minimoRespostas;

          const empresaMedia: EmpresaMedia = {
            id: emp.id,
            nomeFantasia: emp.nomeFantasia,
            cnpj: emp.cnpj,
            responsavel: emp.responsavel ?? { nome: "", telefone: "" },
            telefone: emp.telefone ?? "",
            porte: emp.porte ?? "",
            porteOriginal: emp.porte ?? "",
            endereco: emp.endereco ?? { estado: "" },
            categorias: emp.categorias ?? "",
            media: Number.parseFloat(media.toFixed(DOIS)),
            oficial: Number.parseFloat(oficial.toFixed(DOIS)),
            fase: faseObject?.titulo || "",
            habilitada
          };
          return empresaMedia;
        })
      );

      const uniqueResults = results.filter(
        (item, index, array) => array.findIndex((emp) => emp.id === item.id) === index
      );

      setEmpresasComMedia(uniqueResults);
    } catch (error) {
      console.error(message, error);
      setErrorMedias("Não foi possível carregar os dados das médias. Por favor, tente novamente.");
    } finally {
      setLoadingMedias(false);
    }
  };

  const loadEstadosData = async () => {
    if (empresas.length === 0) return;

    setLoadingEstados(true);
    setErrorEstados(null);
    try {
      const empresaList = await GetEmpresasData(empresas);

      const contagemPorEstado: { [estado: string]: number } = {};

      for (const empresa of empresaList) {
        const estado = (empresa.endereco?.estado as string) ?? "Não informado";
        contagemPorEstado[estado] = (contagemPorEstado[estado] || 0) + 1;
      }

      const resultado: EmpresaPorEstado[] = Object.keys(contagemPorEstado).map((estado) => ({
        estado,
        quantidade: contagemPorEstado[estado] || 0
      }));

      setEmpresasPorEstado(resultado);
    } catch (error) {
      console.error(message, error);
      setErrorEstados("Não foi possível carregar os dados por estado. Por favor, tente novamente.");
    } finally {
      setLoadingEstados(false);
    }
  };

  const getActiveTab = () => {
    const path = location.pathname.split("/").pop();
    if (path === "relatorios") return DEFAULT_TAB;
    return path || DEFAULT_TAB;
  };

  const handleTabChange = useCallback(
    (value: string | null) => {
      navigate(value === DEFAULT_TAB ? "" : value || "");
    },
    [navigate]
  );

  return (
    <>
      <RelatorioComponent setValues={setValues} questionario={values.questionario} />
      <Space h={"xl"} />

      {projeto !== "" && projeto !== null && values.questionario !== "" && values.questionario !== null ? (
        <>
          <Tabs value={getActiveTab()} onChange={handleTabChange}>
            <Tabs.List>
              <Tabs.Tab value="visao-geral">Visão Geral</Tabs.Tab>
              <Tabs.Tab value="empresas">Empresas</Tabs.Tab>
              <Tabs.Tab value="medias">Médias</Tabs.Tab>
              <Tabs.Tab value="estados">Estados</Tabs.Tab>
              <Tabs.Tab value="colaboradores">Colaboradores</Tabs.Tab>
              <Tabs.Tab value="embaixadores">Embaixadores</Tabs.Tab>
            </Tabs.List>

            <Space h="md" />

            <Outlet
              context={
                {
                  empresas,
                  empresasComMedia,
                  empresasPorEstado,
                  loadingEmpresas,
                  loadingMedias,
                  loadingEstados,
                  errorEmpresas,
                  errorMedias,
                  errorEstados,
                  values,
                  selectedDate,
                  setSelectedDate
                } satisfies TRelatorioContext
              }
            />
          </Tabs>
        </>
      ) : (
        <Group justify={"center"}>
          <Text fw={700}>Selecione um projeto e um questionário para buscar!!</Text>
        </Group>
      )}
      <Space h={"xl"} />
    </>
  );
};

export default RelatorioLayout;
